/**
 * Builds structured search filter trees for the NYCBS External PA Requests table.
 */

/** Single predicate condition node for BigQuery structured filters. */
export interface FilterCondition {
  type: "condition";
  field: string;
  operator: string;
  value: string | string[];
}

/** AND/OR group node for BigQuery structured filters. */
export interface FilterGroup {
  type: "group";
  operator: "and" | "or";
  children: (FilterCondition | FilterGroup)[];
}

/**
 * Builds the OR search filter tree matching seq, MRN, patient name, or drug.
 *
 * @param searchTerm - Raw search text from the table search input
 * @return FilterGroup: OR group with seq, patient_mrn, patient_name, and drug conditions
 */
export const buildNycbsSearchFilterTree = (searchTerm: string): FilterGroup => {
  const trimmed = searchTerm.trim();

  return {
    type: "group",
    operator: "or",
    children: [
      {
        type: "condition",
        field: "seq",
        operator: "contains",
        value: trimmed,
      },
      {
        type: "condition",
        field: "patient_mrn",
        operator: "contains",
        value: trimmed,
      },
      {
        type: "condition",
        field: "patient_name",
        operator: "contains",
        value: trimmed,
      },
      {
        type: "condition",
        field: "drug",
        operator: "contains",
        value: trimmed,
      },
    ],
  };
};

/**
 * Converts flat sidebar filters into predicate condition nodes.
 *
 * @param apiFilters - Flat filter map from processFilterData
 * @return FilterCondition[]: Predicate conditions for sidebar filters
 */
export const convertLegacyFiltersToConditions = (
  apiFilters: Record<string, string[] | null>,
): FilterCondition[] => {
  const conditions: FilterCondition[] = [];

  Object.entries(apiFilters).forEach(([field, values]) => {
    if (!values || values.length === 0) {
      return;
    }

    conditions.push({
      type: "condition",
      field,
      operator: "is_any_of",
      value: values,
    });
  });

  return conditions;
};

/**
 * Combines flat sidebar filters with an optional structured filter tree.
 *
 * @param apiFilters - Flat filter map from processFilterData
 * @param structuredFilter - Structured filter tree (e.g. Outcome Class "Others")
 * @return Record<string, string[] | null> | FilterGroup: API-ready filter payload
 */
export const combineNycbsApiFilters = (
  apiFilters: Record<string, string[] | null>,
  structuredFilter?: FilterGroup,
): Record<string, string[] | null> | FilterGroup => {
  if (!structuredFilter) {
    return apiFilters;
  }

  const sidebarConditions: (FilterCondition | FilterGroup)[] =
    convertLegacyFiltersToConditions(apiFilters);
  sidebarConditions.push(structuredFilter);

  if (sidebarConditions.length === 1) {
    return structuredFilter;
  }

  return {
    type: "group",
    operator: "and",
    children: sidebarConditions,
  };
};

/**
 * Merges the search filter tree with any active sidebar filters.
 *
 * @param searchTerm - Raw search text from the table search input
 * @param apiFilters - Flat filter map from processFilterData
 * @param structuredFilter - Structured filter tree (e.g. Outcome Class "Others")
 * @return FilterGroup | Record<string, string[] | null>: Combined or search-only filter tree
 */
export const mergeNycbsSearchFiltersWithSidebarFilters = (
  searchTerm: string,
  apiFilters: Record<string, string[] | null>,
  structuredFilter?: FilterGroup,
): FilterGroup | Record<string, string[] | null> => {
  const searchTree = buildNycbsSearchFilterTree(searchTerm);
  const sidebarConditions: (FilterCondition | FilterGroup)[] =
    convertLegacyFiltersToConditions(apiFilters);

  if (structuredFilter) {
    sidebarConditions.push(structuredFilter);
  }

  if (sidebarConditions.length === 0) {
    return searchTree;
  }

  return {
    type: "group",
    operator: "and",
    children: [...sidebarConditions, searchTree],
  };
};
